import { EntityType } from '@frontend/common';
import { CheckboxInput, TextInput } from '@frontend/components/basic-forms';
import { ICONS, Tag } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { SpotSelectV2 } from '@frontend/spot/select';
import { ShippingNotesDisplay } from '@frontend/transaction/common';
import { FaCheck, FaXmark } from 'react-icons/fa6';
import { LuCheck, LuX } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { useWizardModalScreenNavigation } from '../../wizard-modal-navigation.context';
import useEnterPurchaseOrder from './enter-purchase-order.controller';

const ID = 'create-transaction-purchase-order';

const EnterPurchaseOrder = (props: { wizard_id: string }) => {
    const viewProps = useEnterPurchaseOrder(props);

    useWizardModalScreenNavigation({
        onContinue: viewProps.onSubmit,
        continueDisabled: viewProps.submitDisabled
    });

    return (
        <div
            id={ID}
            className='flex w-full flex-col gap-3'>
            <div className='flex items-start justify-start self-stretch'>
                <div className='text-foreground-default justify-start text-base font-medium'>{CommonMessage.FORMS.PROVIDE_THE_FOLLOWING}</div>
            </div>

            <div
                data-state='Open'
                className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                <div className='inline-flex items-center justify-between self-stretch'>
                    <div className='flex items-center justify-start gap-4'>
                        <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                            {viewProps.resolvedPurchaseOrder ? (
                                <FaCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            ) : (
                                <FaXmark className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            )}
                            <Tag className='size-1/2' />
                        </div>
                        <div className='inline-flex flex-col items-start justify-center gap-1'>
                            <div className='text-foreground-default justify-start text-base font-semibold'>
                                {!viewProps.noResolvablePoNumber && 'UGent'} {CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER}
                            </div>
                        </div>
                    </div>
                </div>
                <TextInput
                    prepend={viewProps.noResolvablePoNumber ? undefined : 42}
                    value={viewProps.poNumber || undefined}
                    onChange={viewProps.setPoNumber}
                    className='mt-4 w-full'
                    placeholder='60014400'
                    isClearable
                    isLoading={viewProps.resolving}
                />
                {!viewProps.noResolvablePoNumber && !!viewProps.poNumber && viewProps.invalidPoNumber && (
                    <div className='text-status-danger font-medium'>
                        <FormattedMessage
                            id='mobile.wizard.enter_purchase_order.po_not_found'
                            defaultMessage='The purchase order was not found. Please make sure the number is correct.'
                        />
                    </div>
                )}

                <div className='inline-flex items-start justify-start'>
                    <CheckboxInput
                        value={viewProps.noResolvablePoNumber}
                        onChange={(value) => {
                            viewProps.setPoNumber('');
                            viewProps.setResolvedPurchaseOrder(null);
                            viewProps.setNoResolvablePoNumber(value);

                            viewProps.updateWizardData({ resolvedPurchaseOrder: null, poNumber: '' });
                        }}
                        label={
                            <FormattedMessage
                                id='mobile.wizard.enter_purchase_order.no_valid_po'
                                defaultMessage="I don't have a UGent purchase order"
                            />
                        }
                    />
                </div>
            </div>

            {(viewProps.noResolvablePoNumber || viewProps.resolvedPurchaseOrder) && (
                <div
                    data-state='Open'
                    className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                    <div className='inline-flex items-center justify-between self-stretch'>
                        <div className='flex items-center justify-start gap-4'>
                            <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                                {viewProps.spotId ? (
                                    <LuCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                ) : (
                                    <LuX className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                )}
                                <ICONS.ENTITY.SPOT className='size-1/2' />
                            </div>
                            <div className='inline-flex flex-col items-start justify-center gap-1'>
                                <div className='text-foreground-default justify-start text-base font-semibold'>{CommonMessage.OBJECTS.SPOT.DISTRI_SPOT}</div>
                            </div>
                        </div>
                    </div>

                    <SpotSelectV2
                        value={viewProps.spotId}
                        onChange={viewProps.setSpotId}
                        hideLabel
                    />
                </div>
            )}

            {viewProps.resolvedPurchaseOrder && !viewProps.noResolvablePoNumber && (
                <div className='flex w-full flex-col gap-2'>
                    <p className='text-foreground-default font-medium'>{CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER}</p>
                    <div className='outline-border-default flex w-full flex-col gap-2 rounded-xl p-4 outline-1'>
                        <div className='inline-flex h-6 items-center justify-between self-stretch'>
                            <div className='text-brand-muted text-base font-medium'>{CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER}</div>
                            <div className='text-foreground-default text-base font-medium'>{viewProps.resolvedPurchaseOrder.po_number}</div>
                        </div>
                        <div className='inline-flex h-6 items-center justify-between self-stretch'>
                            <div className='text-brand-muted text-base font-medium'>{CommonMessage.OBJECTS.SUPPLIER.DEFAULT}</div>
                            <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.resolvedPurchaseOrder.supplier ?? '/'}</div>
                        </div>
                        <div className='inline-flex h-6 items-center justify-between self-stretch'>
                            <div className='text-brand-muted text-base font-medium'>{CommonMessage.OBJECTS.TRANSACTION.RECEIVER}</div>
                            <PlatformObjectBadge
                                id={viewProps.resolvedPurchaseOrder.receiver_id || undefined}
                                className='text-foreground-default flex items-center gap-1 text-base font-medium'
                                entityType={EntityType.USER}
                                disableClick
                                key={viewProps.resolvedPurchaseOrder.receiver_id}
                            />
                        </div>
                    </div>
                </div>
            )}

            {viewProps.resolvedPurchaseOrder?.shipping_notes && (
                <>
                    <div className='flex items-start justify-start self-stretch'>
                        <div className='text-foreground-default justify-start text-base font-medium'>{CommonMessage.OBJECTS.TRANSACTION.SHIPPING_NOTES}</div>
                    </div>
                    <div
                        data-state='Open'
                        className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                        <div className='inline-flex items-center justify-between self-stretch'>
                            <div className='flex items-center justify-start gap-4'>
                                <ShippingNotesDisplay value={viewProps.resolvedPurchaseOrder.shipping_notes} />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default EnterPurchaseOrder;
